import {
	Component,
	ContentChild,
	ContentChildren,
	EventEmitter,
	Input,
	Output,
	QueryList,
	TemplateRef
} from '@angular/core';
import { DataTableColumn } from './data-table-column';
import { ColumnCellDirective } from './column-cell.directive';
import { TableFooterDirective } from './table-footer.directive';

// Reusable, presentational data table: sortable headers, per-column cell templates,
// an optional footer (aggregation) row, and built-in pagination + page-size selector.
// State (page/sort/pageSize) is owned by the parent; the table only emits intentions.
@Component({
	selector: 'app-data-table',
	templateUrl: './data-table.component.html',
	styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent {
	@Input() columns: DataTableColumn[] = [];
	@Input() rows: any[] = [];
	@Input() total = 0;
	@Input() page = 1;
	@Input() pageSize = 100;
	@Input() pageSizeOptions: number[] = [5, 10, 20, 50, 100];
	@Input() sortColumn: string = null;
	@Input() sortDirection: 'asc' | 'desc' = 'asc';
	// Already-translated noun for the range text ("… of N {items}"), e.g. "carehomes".
	@Input() itemsLabel = '';
	// Property used to track rows in *ngFor.
	@Input() trackByKey = 'Id';

	@Output() sortChange = new EventEmitter<string>();
	@Output() pageChange = new EventEmitter<number>();
	@Output() pageSizeChange = new EventEmitter<number>();
	// Row clicked (not on a link, button or form control inside it). Rows only look clickable when bound.
	@Output() rowClick = new EventEmitter<any>();

	@ContentChildren(ColumnCellDirective) cells: QueryList<ColumnCellDirective>;
	@ContentChild(TableFooterDirective) footer: TableFooterDirective;

	cellTemplate(key: string): TemplateRef<any> | null {
		const match = this.cells ? this.cells.find(c => c.key === key) : null;
		return match ? match.template : null;
	}

	get totalPages(): number {
		return Math.max(1, Math.ceil(this.total / this.pageSize));
	}

	get pageNumbers(): number[] {
		return Array.from({ length: this.totalPages }, (_, i) => i + 1);
	}

	get rangeStart(): number {
		return this.total === 0 ? 0 : (this.page - 1) * this.pageSize + 1;
	}

	get rangeEnd(): number {
		return Math.min(this.page * this.pageSize, this.total);
	}

	trackRow = (index: number, row: any): any =>
		this.trackByKey && row != null ? row[this.trackByKey] : index;

	sortIndicator(key: string): string {
		if (this.sortColumn !== key) {
			return '↕';
		}
		return this.sortDirection === 'asc' ? '↑' : '↓';
	}

	onSort(column: DataTableColumn): void {
		if (column.sortable) {
			this.sortChange.emit(column.key);
		}
	}

	onPage(page: number): void {
		if (page < 1 || page > this.totalPages || page === this.page) {
			return;
		}
		this.pageChange.emit(page);
	}

	get rowsClickable(): boolean {
		return this.rowClick.observers.length > 0;
	}

	onRowClick(row: any, event: MouseEvent): void {
		if (!this.rowsClickable) {
			return;
		}
		// Controls inside the row keep their own click; selecting text is not a click either.
		const target = event.target as HTMLElement;
		if (target?.closest('a, button, input, select, textarea, label')) {
			return;
		}
		const selection = window.getSelection();
		if (selection && selection.toString().length > 0) {
			return;
		}
		this.rowClick.emit(row);
	}

	onPageSize(size: number): void {
		this.pageSizeChange.emit(Number(size) || this.pageSizeOptions[0]);
	}
}
